import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { getSessionId, setSessionId as storeSessionId } from "../utils/sessionUtils";
import { AuthContext } from "../contexts/AuthContext";
import { post, get } from "../utils/apiClient";
import { logger } from "../utils/logger";

/**
 * useConversationSession — the single canonical frontend AI session driver.
 *
 * Every AI surface (the main Assistant chat via ConversationContext, and the
 * isolated Projects writing workspace via useWorkProductSession) drives its
 * conversation lifecycle through this hook and the shared primitives below,
 * so there is no parallel session driver that can drift independently.
 *
 * Two modes:
 *  - persisted (default): the active session id is mirrored to sessionStorage
 *    and rehydrated on mount; messages are auto-fetched.  Used by the main chat.
 *  - isolated ({ persist: false }): the session lives only for the lifetime of
 *    the hook, never touches storage, and does not auto-rehydrate or auto-fetch.
 *    Used by the Projects writing workspace, which needs a fresh, isolated
 *    session per workspace open.
 */

const DEFAULT_AI_TIMEOUT_MS = 120000;
const DEFAULT_MAX_AI_RETRIES = 3;
const DEFAULT_AI_RETRY_BASE_DELAY = 1000;

export interface AiRespondOptions {
  /** Abort signal used to cancel the in-flight request and short-circuit retries. */
  signal?: AbortSignal;
  timeoutMs?: number;
  maxRetries?: number;
  baseDelayMs?: number;
  /** Apply +/-20% jitter to the backoff delay (the main chat uses this). */
  jitter?: boolean;
  /** Called before a scheduled retry with the human attempt number, total attempts, and wait time. */
  onRetryScheduled?: (nextAttemptHuman: number, totalAttempts: number, waitMs: number) => void;
  /** Called once when a request succeeds after at least one retry. */
  onRetrySucceeded?: (attempt: number) => void;
}

export function isServerError(e: unknown): boolean {
  const status = (e as { status?: number })?.status;
  return status !== undefined && status >= 500;
}

function isNetworkError(e: unknown): boolean {
  const status = (e as { status?: number })?.status;
  const message = String((e as { message?: string })?.message || "");
  return (
    !status &&
    (e instanceof TypeError ||
      message.includes("Failed to fetch") ||
      message.includes("NetworkError") ||
      message === "Network request failed")
  );
}

/**
 * Shared AI request driver for POST /api/engine/respond.
 *
 * This is the single implementation of the send + retry/backoff + cancellation
 * mechanics used by every AI surface.  It performs no UI/toast/intent work —
 * callers supply onRetryScheduled/onRetrySucceeded to render their own feedback
 * and handle the thrown error (AbortError on cancellation, or the last error
 * after retries are exhausted) in a domain-appropriate way.
 */
export async function requestAiResponse(
  bodyPayload: Record<string, unknown>,
  options: AiRespondOptions = {},
): Promise<unknown> {
  const {
    signal,
    timeoutMs = DEFAULT_AI_TIMEOUT_MS,
    maxRetries = DEFAULT_MAX_AI_RETRIES,
    baseDelayMs = DEFAULT_AI_RETRY_BASE_DELAY,
    jitter = false,
    onRetryScheduled,
    onRetrySucceeded,
  } = options;

  let lastError: unknown = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    try {
      const res = await post(`/api/engine/respond`, bodyPayload, { timeout: timeoutMs, signal });
      const data = await res.json();
      if (attempt > 0) {
        onRetrySucceeded?.(attempt);
      }
      return data;
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        throw e;
      }
      lastError = e;
      const retryable = isServerError(e) || isNetworkError(e);
      if (retryable && attempt < maxRetries) {
        const delay = baseDelayMs * Math.pow(2, attempt);
        const jittered = jitter ? delay + delay * 0.2 * (Math.random() * 2 - 1) : delay;
        const waitMs = Math.max(200, Math.round(jittered));
        logger.warn(
          `AI request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${waitMs}ms...`,
        );
        onRetryScheduled?.(attempt + 2, maxRetries + 1, waitMs);
        await new Promise((resolve) => setTimeout(resolve, waitMs));
        continue;
      }
      throw e;
    }
  }

  // Only reached if the loop exits without returning/throwing (defensive).
  throw lastError ?? new Error("AI request failed");
}

/**
 * Create a new conversation session (POST /api/conversations/new).
 *
 * Shared by both modes so there is a single session-creation implementation.
 * When throwOnAuthError is true (persisted/main-chat mode) a 401 raises
 * "Not authenticated" so callers can drive a demo-login fallback; when false
 * (isolated mode) a 401 resolves to null.
 */
export async function createConversationSession(
  options: { throwOnAuthError?: boolean } = {},
): Promise<string | null> {
  const { throwOnAuthError = true } = options;
  const res = await post("/api/conversations/new", {});

  if (res.status === 401) {
    logger.log("[SESSION_REHYDRATE_DEBUG] create_session_failed status=401 unauthenticated");
    if (throwOnAuthError) {
      throw new Error("Not authenticated");
    }
    return null;
  }

  const data = await res.json();
  const newId = data.session_id;
  if (!newId) {
    logger.log("[SESSION_REHYDRATE_DEBUG] create_session_no_id");
    return null;
  }
  return newId;
}

export interface UseConversationSessionOptions {
  /** Mirror the session id to sessionStorage and rehydrate/auto-fetch. Default true. */
  persist?: boolean;
  /** Restore a stored session id on mount. Defaults to `persist`. */
  autoRehydrate?: boolean;
  /** Fetch messages whenever the session id changes. Defaults to `persist`. */
  autoFetchMessages?: boolean;
}

export function useConversationSession(options: UseConversationSessionOptions = {}) {
  const {
    persist = true,
    autoRehydrate = persist,
    autoFetchMessages = persist,
  } = options;

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rehydrating, setRehydrating] = useState(autoRehydrate);
  const hasFetchedMessagesRef = useRef(false);
  // Mirror of the current session id so isolated consumers can read it
  // synchronously (e.g. sending a message immediately after creation).
  const sessionIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  const applySessionId = useCallback(
    (id: string | null) => {
      sessionIdRef.current = id;
      setSessionId(id);
      if (persist && id) {
        storeSessionId(id);
      }
    },
    [persist],
  );

  useEffect(() => {
    if (!autoRehydrate) return;
    const stored = getSessionId();
    if (stored) {
      logger.log("[SESSION_REHYDRATE_DEBUG] restore_from_storage=success session_id=", stored);
      sessionIdRef.current = stored;
      setSessionId(stored);
    } else {
      logger.log("[SESSION_REHYDRATE_DEBUG] restore_from_storage=none");
    }
    setRehydrating(false);
  }, [autoRehydrate]);

  const createNewSession = useCallback(async () => {
    if (rehydrating) {
      logger.log("[SESSION_REHYDRATE_DEBUG] block_create_while_rehydrating");
      return null;
    }
    if (persist && (!user || !user.uuid)) {
      logger.warn("[SESSION_REHYDRATE_DEBUG] create_blocked_missing_user");
      throw new Error("Not authenticated");
    }

    setLoading(true);
    try {
      const newId = await createConversationSession({ throwOnAuthError: persist });
      if (!newId) {
        return null;
      }
      applySessionId(newId);
      logger.log("[SESSION_REHYDRATE_DEBUG] create_session_success session_id=", newId);
      return newId;
    } finally {
      setLoading(false);
    }
  }, [rehydrating, user, persist, applySessionId]);

  const fetchMessages = useCallback(async (force = false) => {
    if (!sessionId) return [];
    if (hasFetchedMessagesRef.current && !force) {
      logger.log("[SESSION_REHYDRATE_DEBUG] skip_fetch_messages_already_fetched");
      return [];
    }
    hasFetchedMessagesRef.current = true;
    setLoading(true);
    try {
      logger.log("[SESSION_REHYDRATE_DEBUG] fetching_messages session_id=", sessionId);
      const res = await get(`/api/conversations/${sessionId}/messages`);
      if (!res.ok) {
        logger.log("[SESSION_REHYDRATE_DEBUG] fetch_messages_failed status=", res.status);
        return [];
      }
      const data = await res.json();
      const msgs = (data && Array.isArray(data.messages)) ? data.messages : [];
      setMessages(msgs);
      logger.log("[SESSION_REHYDRATE_DEBUG] fetch_messages_success count=", msgs.length);
      return msgs;
    } catch (err) {
      logger.log("[SESSION_REHYDRATE_DEBUG] fetch_messages_exception=", String(err));
      return [];
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (autoFetchMessages && sessionId) {
      fetchMessages();
    }
  }, [autoFetchMessages, sessionId, fetchMessages]);

  /**
   * Send a message through the shared AI request driver, bound to this hook's
   * abort controller so `cancel()` cancels the in-flight request.
   */
  const respond = useCallback(async (bodyPayload: Record<string, unknown>, opts: AiRespondOptions = {}) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return await requestAiResponse(bodyPayload, { ...opts, signal: controller.signal });
  }, []);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  /** Clear the active session (and abort any in-flight request). */
  const resetSession = useCallback(() => {
    abortControllerRef.current?.abort();
    hasFetchedMessagesRef.current = false;
    sessionIdRef.current = null;
    setSessionId(null);
  }, []);

  const getCurrentSessionId = useCallback(() => sessionIdRef.current, []);

  return {
    sessionId,
    messages,
    loading,
    rehydrating,
    createNewSession,
    fetchMessages,
    respond,
    cancel,
    resetSession,
    getCurrentSessionId,
  };
}
